"use client";
import React, { createContext, useContext, useState, useCallback } from "react";
import Notification from "./Notification";

interface NotificationContextType {
  showNotification: (
    variant: "success" | "info" | "warning" | "error",
    title: string,
    description?: string,
    duration?: number
  ) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification must be used within NotificationProvider");
  }
  return context;
};

interface NotificationData {
  id: string;
  variant: "success" | "info" | "warning" | "error";
  title: string;
  description?: string;
  duration?: number;
}

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<NotificationData[]>([]);

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const showNotification = useCallback(
    (
      variant: "success" | "info" | "warning" | "error",
      title: string,
      description?: string,
      duration: number = 12000 // Increased default to 12 seconds for better readability
    ) => {
      // Generate unique ID using timestamp + random number to prevent duplicates
      const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const notification: NotificationData = { id, variant, title, description, duration };

      setNotifications((prev) => [...prev, notification]);

      // Auto-remove after duration
      setTimeout(() => {
        removeNotification(id);
      }, duration);
    },
    [removeNotification]
  );

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}
      
      {/* Notification Container - Centered on mobile, Top Right on desktop.
          role/aria-live: for several flows a toast is the ONLY signal that an
          action succeeded or failed — the save-time tuition copy (#2467) shows
          nothing else on failure — so without this a screen-reader user reads
          the click as having done nothing. */}
      {/* STATIC role/aria-live on purpose. Flipping them per notification
          re-registers the live region in the same commit that inserts the text,
          and screen readers then commonly announce nothing — which would break
          the one flow this was added for (#2467's copy failure shows no other
          signal). Errors get their interrupt from role="alert" on the toast
          itself instead. */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="false"
        className="fixed top-20 left-1/2 -translate-x-1/2 sm:left-auto sm:right-4 sm:translate-x-0 z-[9999999] space-y-3 w-[calc(100%-32px)] sm:w-auto max-w-[400px] flex flex-col items-center sm:items-end"
      >
        {notifications.map((notification) => (
          <Notification
            key={notification.id}
            variant={notification.variant}
            title={notification.title}
            description={notification.description}
            hideDuration={notification.duration}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </NotificationContext.Provider>
  );
};
